// Polls an asynchronous operation (202 + operation_id) until it settles, with backoff.
// Used by provider tests and probes: the result shown is the recorded fact, never a guess.
import { useCallback, useEffect, useRef, useState } from "react";
import type { AcceptedOperation } from "@maestros/contracts";
import { getOperation } from "../api/endpoints.ts";
import type { OperationRecord } from "../api/types.ts";

export interface OperationState {
  status: OperationRecord["status"];
  result: OperationRecord["result"];
  error: OperationRecord["error"];
  operationId: string;
}

export function useOperation() {
  const [op, setOp] = useState<OperationState | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<number | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, []);

  const poll = useCallback((id: string, attempt: number) => {
    timer.current = window.setTimeout(
      async () => {
        try {
          const next = await getOperation(id);
          if (!alive.current) return;
          setOp({ status: next.status, result: next.result, error: next.error, operationId: id });
          if (next.status === "accepted" || next.status === "running") poll(id, attempt + 1);
          else setBusy(false);
        } catch (e) {
          if (!alive.current) return;
          setError(e);
          setBusy(false);
        }
      },
      Math.min(4000, 300 * 2 ** Math.min(attempt, 4)),
    );
  }, []);

  /** Runs the request that answers 202; refusals (409/403/422) are kept as the error. */
  const run = useCallback(
    async (start: () => Promise<AcceptedOperation>) => {
      if (timer.current !== null) window.clearTimeout(timer.current);
      setBusy(true);
      setError(null);
      setOp(null);
      try {
        const accepted = await start();
        if (!alive.current) return;
        setOp({ status: accepted.status, result: null, error: null, operationId: accepted.operation_id });
        if (accepted.status === "accepted" || accepted.status === "running") poll(accepted.operation_id, 0);
        else setBusy(false);
      } catch (e) {
        if (!alive.current) return;
        setError(e);
        setBusy(false);
      }
    },
    [poll],
  );

  const reset = useCallback(() => {
    setOp(null);
    setError(null);
  }, []);

  return { op, error, busy, run, reset };
}
